import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import BottomNavBar from "../components/layout/BottomNav";
import Header from "../components/layout/Header";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useFirebaseData } from "../hooks/useFirebaseData";
import { isUnderAge } from "../utils/dateUtils";
import {
  lookupUsername,
  followUser,
  unfollowUser,
  getFollowers,
  getFollowingList,
  UsernameData,
} from "../services/connections";
import { getUserPublicData, userService } from "../services/userService";
import { trainingTypeService } from "../services/trainingTypeService";
import { UserPublicData } from "../types/user";
import { Timestamp, doc, getDocFromServer } from "firebase/firestore";
import { db } from "../firebase/config";
import { getFunctions, httpsCallable } from "firebase/functions";
import PostCard from "../components/PostCard";
import { useFeed } from "../hooks/useFeed";
import { getUserPostsPaginated } from "../services/postService";

// COMMENTED OUT: Suggested Athletes
// import {
//   getSuggestedAthletes,
//   SuggestedAthleteWithData,
// } from "../services/suggestedAthletesService";

// NEW: Import our Zustand stores (not using yet - just preparing)
import {
  useConnections,
  // useSuggestedAthletes, // COMMENTED OUT: Suggested Athletes
  useUserProfiles,
} from "../stores";

interface UserWithPublicData {
  userId: string;
  publicData: UserPublicData | null;
}
const DAY_MS = 86_400_000;

interface SocialBlockDetails {
  isBlocked?: boolean;
  blockedReason?: string;
  blockedReasonCategory?: "legal" | "community";
  blockedAt?: Timestamp;
  blockedDuration?: number | null; // null = permanent
}

// Client-computed expiry: suspended while isBlocked AND (permanent OR not past end).
const isSuspensionActive = (b?: SocialBlockDetails | null): boolean => {
  if (!b || !b.isBlocked) return false;
  if (b.blockedDuration == null) return true; // permanent
  const startMs = b.blockedAt?.toMillis?.() ?? 0;
  return startMs + b.blockedDuration * DAY_MS > Date.now();
};
// Animation variants for enhanced UX
const searchFieldVariants = {
  idle: {
    scale: 1,
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    borderColor: "oklch(var(--b3))",
  },
  hover: {
    scale: 1.005,
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    borderColor: "oklch(var(--bc) / 0.3)",
    transition: { duration: 0.2 },
  },
  tap: {
    scale: 0.98,
    transition: { duration: 0.1 },
  },
};

const searchIconVariants = {
  idle: { rotate: 0, scale: 1 },
  hover: { rotate: 0, scale: 1.05, transition: { duration: 0.2 } },
  tap: { rotate: 0, scale: 0.95, transition: { duration: 0.1 } },
};

// Create animation variants that respect reduced motion
const createAnimationVariants = (prefersReducedMotion: boolean) => ({
  athleteCard: {
    hidden: prefersReducedMotion
      ? { opacity: 0 }
      : {
          opacity: 0,
          y: 20,
          scale: 0.95,
        },
    visible: (index: number) =>
      prefersReducedMotion
        ? {
            opacity: 1,
            transition: { duration: 0 },
          }
        : {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              delay: index * 0.05,
              duration: 0.3,
            },
          },
    hover: prefersReducedMotion
      ? {}
      : {
          y: -2,
          scale: 1.02,
          boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
          transition: { duration: 0.2 },
        },
  },
  followButton: {
    idle: { scale: 1 },
    hover: prefersReducedMotion ? {} : { scale: 1.05 },
    tap: prefersReducedMotion ? {} : { scale: 0.95 },
  },
});

// Skeleton Components
const SearchFieldSkeleton = () => (
  <div className="px-4 mb-3">
    <div className="w-full bg-base-300 rounded-lg p-3 flex items-center gap-2.5 animate-pulse">
      <div className="h-4 w-4 bg-base-300 rounded-full"></div>
      <div className="h-4 bg-base-300 rounded w-32"></div>
    </div>
  </div>
);

const AthleteCardSkeleton = ({ index }: { index: number }) => (
  <motion.div
    className="flex-shrink-0 bg-base-100 rounded-lg shadow-sm border border-base-300 p-5 w-44"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05, duration: 0.3 }}
  >
    <div className="animate-pulse">
      {/* Profile Image Skeleton */}
      <div className="w-16 h-16 bg-base-300 rounded-full mx-auto mb-3"></div>

      {/* Name Skeleton */}
      <div className="h-4 bg-base-300 rounded w-24 mx-auto mb-2"></div>

      {/* Sport Skeleton */}
      <div className="h-3 bg-base-300 rounded w-16 mx-auto mb-4"></div>

      {/* Button Skeleton */}
      <div className="h-10 bg-base-300 rounded w-full"></div>
    </div>
  </motion.div>
);

const PostCardSkeleton = ({ index }: { index: number }) => (
  <motion.div
    className="bg-base-100 rounded-lg border border-base-300 p-4 mb-4"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1, duration: 0.3 }}
  >
    <div className="animate-pulse">
      {/* Header */}
      <div className="flex items-center mb-3">
        <div className="w-10 h-10 bg-base-300 rounded-full mr-3"></div>
        <div>
          <div className="h-4 bg-base-300 rounded w-20 mb-1"></div>
          <div className="h-3 bg-base-300 rounded w-16"></div>
        </div>
      </div>

      {/* Content */}
      <div className="h-4 bg-base-300 rounded w-full mb-2"></div>
      <div className="h-4 bg-base-300 rounded w-3/4 mb-3"></div>

      {/* Image */}
      <div className="h-48 bg-base-300 rounded mb-3"></div>

      {/* Actions */}
      <div className="flex justify-between">
        <div className="h-8 bg-base-300 rounded w-16"></div>
        <div className="h-8 bg-base-300 rounded w-16"></div>
      </div>
    </div>
  </motion.div>
);

const Connections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData } = useAuth();

  // Social features are restricted to users aged 16 and above.
  const isRestrictedByAge = isUnderAge(userData?.dob, 16);
  // Social-feature suspension (block from social) — always re-checked against
  // the server on mount so a suspension/lift applied elsewhere in the session
  // (e.g. by an admin) takes effect without requiring a full app refresh.
  // Cached AuthContext userData is intentionally NOT used here.
  const [freshSocialBlock, setFreshSocialBlock] = useState<
    SocialBlockDetails | null
  >(null);
  const [suspensionCheckLoading, setSuspensionCheckLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) {
      setSuspensionCheckLoading(false);
      return;
    }
    let cancelled = false;
    setSuspensionCheckLoading(true);
    getDocFromServer(doc(db, "users", currentUser.uid, "private", "socialBlockDetails"))
      .then((snap) => {
        if (cancelled) return;
        setFreshSocialBlock((snap.data() as SocialBlockDetails | undefined) ?? null);
      })
      .catch((e) => {
        console.error("Error fetching fresh suspension status:", e);
      })
      .finally(() => {
        if (!cancelled) setSuspensionCheckLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  const socialBlock = freshSocialBlock ?? undefined;
  const isSuspended = isSuspensionActive(socialBlock);

  const [suspensionReasonLabels, setSuspensionReasonLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isSuspended) return;
    const fn = httpsCallable(getFunctions(), "getReportOptions");
    fn()
      .then((res) => {
        const data = res.data as { reasons: { value: string; label: string }[] };
        const map: Record<string, string> = {};
        (data.reasons ?? []).forEach((r) => (map[r.value] = r.label));
        setSuspensionReasonLabels(map);
      })
      .catch((e) => console.error("Error fetching report options:", e));
  }, [isSuspended]);

  // Admin viewing: support /connections?uid={otherUserId}
  const queryUid = useMemo(
    () => new URLSearchParams(location.search).get("uid"),
    [location.search],
  );

  // The user whose feed/connections we're actually viewing.
  const effectiveUserId = queryUid || currentUser?.uid || null;
  const isViewingAsAdmin = !!queryUid && queryUid !== currentUser?.uid;

  // If viewing another user's feed, fetch their public profile for the banner
  const { data: viewedUser } = useFirebaseData<UserPublicData>({
    collection: "users",
    docId: queryUid || "",
    enabled: !!queryUid,
    maxAge: 600,
    enableRealtimeUpdates: false,
    fallbackToCache: true,
  });

  // Tori message state
  const [showToriMessage, setShowToriMessage] = useState(false);
  const [checkingToriMessage, setCheckingToriMessage] = useState(true);
  const [currentUserPublicData, setCurrentUserPublicData] =
    useState<UserPublicData | null>(null);
  const [isFollowingTori, setIsFollowingTori] = useState(false);
  const [isDismissingMessage, setIsDismissingMessage] = useState(false);
  const [toriFollowSuccess, setToriFollowSuccess] = useState(false);
  const [toriFollowError, setToriFollowError] = useState<string | null>(null);

  // Sport names for display, resolved from the sports collection
  const [availableSports, setAvailableSports] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    trainingTypeService
      .fetchSports()
      .then((allSports) => {
        const sportsMap = allSports.reduce(
          (acc: Record<string, string>, sport) => {
            if (sport.id) acc[sport.id] = sport.name;
            return acc;
          },
          {},
        );
        setAvailableSports(sportsMap);
      })
      .catch((err) => console.error("Error fetching sports:", err));
  }, []);

  // Resolve a user's sport names for display from their trainingSportIDs
  const getSportNames = (publicData?: {
    trainingSportIDs?: string[];
  } | null): string =>
    (publicData?.trainingSportIDs || [])
      .map((id) => availableSports[id])
      .filter(Boolean)
      .join(", ");

  // Respect user's reduced motion preference
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Get animation variants based on motion preference
  const animationVariants = createAnimationVariants(prefersReducedMotion);

  // NEW: Initialize store hooks (shadow mode - not using yet)
  const connectionState = useConnections(effectiveUserId);
  // COMMENTED OUT: Suggested Athletes
  // const suggestedState = useSuggestedAthletes(
  //   currentUser?.uid || null,
  //   connectionState.following
  // );
  const profileState = useUserProfiles();

  // NEW: Debug log to verify stores are working (will remove later)
  // console.log("🏪 Store Debug:", {
  //   connections: connectionState.connections,
  //   // suggestedAthletes: suggestedState.athletes, // COMMENTED OUT: Suggested Athletes
  //   profileCount: profileState.profileCount,
  // });

  // Feed data with pagination
  const {
    feedEntries,
    loading: feedLoading,
    error: feedError,
    hasMore: feedHasMore,
    loadMore: loadMoreFeed,
    refresh: refreshFeed,
  } = useFeed({
    limit: 10,
    enablePagination: true,
    userId: effectiveUserId,
  });

  // Search and connection state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [foundUser, setFoundUser] = useState<
    (UsernameData & { publicData?: UserPublicData }) | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isFoundUserFollowed, setIsFoundUserFollowed] = useState(false);
  // const [followingIds, setFollowingIds] = useState<string[]>([]); // MIGRATED to store

  // NEW: Use store data for following IDs
  const followingIds = connectionState.following;

  // Followers and Following counts (MIGRATED to store)
  // const [followerCount, setFollowerCount] = useState(0);
  // const [followingCount, setFollowingCount] = useState(0);

  // NEW: Use store data for counts
  const followerCount = connectionState.followerCount;
  const followingCount = connectionState.followingCount;

  // Dialog states for followers/following
  const [isFollowersDialogOpen, setIsFollowersDialogOpen] = useState(false);
  const [isFollowingDialogOpen, setIsFollowingDialogOpen] = useState(false);
  const [followers, setFollowers] = useState<UserWithPublicData[]>([]);
  const [following, setFollowing] = useState<UserWithPublicData[]>([]);
  const [isLoadingFollowers, setIsLoadingFollowers] = useState(false);
  const [isLoadingFollowing, setIsLoadingFollowing] = useState(false);
  const [unfollowingUserId, setUnfollowingUserId] = useState<string | null>(
    null,
  );
  const [followingFollowerId, setFollowingFollowerId] = useState<string | null>(
    null,
  );

  // REMOVED: User posts state - now only loading feed data for faster initial load
  // const [userPosts, setUserPosts] = useState<(Post & { id: string })[]>([]);
  // const [isLoadingUserPosts, setIsLoadingUserPosts] = useState(false);
  // const [userPostsError, setUserPostsError] = useState<string | null>(null);
  // const [userPostsHasMore, setUserPostsHasMore] = useState(true);
  // const [lastUserPostTimestamp, setLastUserPostTimestamp] = useState<
  //   Date | undefined
  // >();

  // Pull to refresh state
  const [isPullToRefresh, setIsPullToRefresh] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [canPull, setCanPull] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Thresholds for pull to refresh
  const REFRESH_THRESHOLD = 80;
  const [lastScrollY, setLastScrollY] = useState(0);
  const [postLoadingId, setPostLoadingId] = useState<string | null>(null);

  // COMMENTED OUT: Suggested Athletes state (MIGRATED to store)
  // const [suggestedAthletes, setSuggestedAthletes] = useState<(SuggestedAthleteWithData & { isFollowed: boolean, isDismissed: boolean })[]>([]);
  // const [isLoadingSuggestedAthletes, setIsLoadingSuggestedAthletes] = useState(true);
  // const [followingSuggestedAthleteId, setFollowingSuggestedAthleteId] =
  //   useState<string | null>(null);

  // COMMENTED OUT: NEW: Use store data for suggested athletes
  // const suggestedAthletes = suggestedState.athletes;

  // COMMENTED OUT: DEBUG: Monitor changes to suggested athletes
  // useEffect(() => {
  //   console.log(
  //     "📊 Suggested athletes updated:",
  //     suggestedAthletes.map((a) => ({
  //       username: a.publicData?.username,
  //       userId: a.userId.slice(-4),
  //       isFollowed: a.isFollowed,
  //       isDismissed: a.isDismissed,
  //     }))
  //   );
  // }, [suggestedAthletes]);

  // NEW: Master unified loading state (eliminates multiple loading indicators)
  const isGlobalLoading =
    connectionState.isLoading ||
    // suggestedState.isLoading || // COMMENTED OUT: Suggested Athletes
    feedLoading;
  // REMOVED: Load user posts - now only loading feed data for faster initial load
  // const loadUserPosts = async (isRefresh = false) => {
  //   if (!currentUser || isLoadingUserPosts) return;
  //   if (!isRefresh && !userPostsHasMore) return;
  //
  //   setIsLoadingUserPosts(true);
  //   setUserPostsError(null);
  //
  //   try {
  //     const posts = await getUserPostsPaginated(
  //       currentUser.uid,
  //       10,
  //       isRefresh ? undefined : lastUserPostTimestamp
  //     );
  //
  //     if (isRefresh) {
  //       setUserPosts(posts);
  //       setLastUserPostTimestamp(undefined);
  //     } else {
  //       setUserPosts((prev) => [...prev, ...posts]);
  //     }
  //
  //     // Update pagination state
  //     setUserPostsHasMore(posts.length === 10);
  //     if (posts.length > 0) {
  //       const lastPost = posts[posts.length - 1];
  //       const timestamp = lastPost.postTimestamp?.toDate?.() || new Date(0);
  //       setLastUserPostTimestamp(timestamp);
  //     }
  //   } catch (error) {
  //     console.error("Error loading user posts:", error);
  //     setUserPostsError("Failed to load your posts");
  //   } finally {
  //     setIsLoadingUserPosts(false);
  //   }
  // };

  // Load current following list (REMOVED: Store handles this automatically)
  // const loadFollowingList = async () => {
  //   if (!currentUser) return;

  //   try {
  //     const followingList = await getFollowingList(currentUser.uid);
  //     console.log('Updated following list:', followingList);
  //   } catch (error) {
  //     console.error("Error loading following list:", error);
  //   }
  // };

  // Check if user has read the Tori message
  useEffect(() => {
    const checkToriMessage = async () => {
      if (!currentUser) {
        setCheckingToriMessage(false);
        return;
      }

      try {
        const publicData = await getUserPublicData(currentUser.uid);
        setCurrentUserPublicData(publicData);

        // Show message if socialMessageRead is not true
        setShowToriMessage(!publicData?.socialMessageRead);
      } catch (error) {
        console.error("Error checking Tori message status:", error);
      } finally {
        setCheckingToriMessage(false);
      }
    };

    checkToriMessage();
  }, [currentUser]);

  // REMOVED: This was duplicate fetching - the connectionStore already loads this data
  // via useConnections hook. Keeping the comment for reference.
  // useEffect(() => {
  //   const loadData = async () => {
  //     if (!currentUser) return;
  //     try {
  //       const [followersList, followingList] = await Promise.all([
  //         getFollowers(currentUser.uid),
  //         getFollowingList(currentUser.uid),
  //       ]);
  //     } catch (error) {
  //       console.error("Error loading connection counts:", error);
  //     }
  //   };
  //   loadData();
  // }, [currentUser]);

  // Load suggested athletes (REMOVED: Store handles this automatically)
  // useEffect(() => {
  //   const loadSuggestedAthletes = async () => {
  //     if (!currentUser) return;
  //
  //     try {
  //       setIsLoadingSuggestedAthletes(true);
  //       console.log('Loading suggested athletes...');
  //       const athletes = await getSuggestedAthletes();
  //       console.log('Loaded suggested athletes:', athletes.length, athletes);
  //
  //       // Filter out the current user and map to include isFollowed and isDismissed flags
  //       const athletesWithFlags = athletes
  //         .filter(athlete => athlete.userId !== currentUser.uid) // Exclude current user
  //         .map(athlete => ({
  //           ...athlete,
  //           isFollowed: followingIds.includes(athlete.userId),
  //           isDismissed: false
  //         }));
  //
  //       console.log('Suggested athletes with flags (excluding self):', athletesWithFlags);
  //       setSuggestedAthletes(athletesWithFlags);
  //     } catch (error) {
  //       console.error('Error loading suggested athletes:', error);
  //     } finally {
  //       setIsLoadingSuggestedAthletes(false);
  //     }
  //   };

  //   loadSuggestedAthletes();
  // }, [followingIds, currentUser]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError("Please enter a username");
      return;
    }

    setIsSearching(true);
    setError(null);
    setFoundUser(null);
    setSuccessMessage(null);

    try {
      const usernameData = await lookupUsername(searchQuery.trim());

      if (!usernameData) {
        setError("User not found");
        setIsSearching(false);
        return;
      }

      const publicData = await getUserPublicData(usernameData.userId);

      // Check if the user has a private profile
      if (publicData?.isPrivateProfile === true) {
        setError("This profile is private and cannot be found in search");
        setIsSearching(false);
        return;
      }

      setFoundUser({
        ...usernameData,
        publicData: publicData || undefined,
      });

      // Check if this user is already being followed
      setIsFoundUserFollowed(followingIds.includes(usernameData.userId));
    } catch (err) {
      console.error("Error searching for user:", err);
      setError("Failed to search for user");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddUser = async () => {
    if (!currentUser || !foundUser) return;

    setIsAdding(true);
    setError(null);

    try {
      if (isFoundUserFollowed) {
        // Optimistically hide the user's posts and flip the UI state right away
        // so the action feels instant. The Cloud Function call below still
        // waits for the unfollow + feed cleanup to fully complete.
        removePostsFromUnfollowedUser(foundUser.userId);
        setIsFoundUserFollowed(false);

        try {
          await connectionState.unfollowUser(foundUser.userId);
          setSuccessMessage(`Successfully unfollowed ${foundUser.username}!`);
        } catch (err) {
          // Revert optimistic hide on failure so we don't lose the posts.
          setRecentlyUnfollowedId((curr) =>
            curr === foundUser.userId ? null : curr,
          );
          setIsFoundUserFollowed(true);
          throw err;
        }
      } else {
        // Optimistic follow flow:
        //   1. Kick off the real `followUser` Cloud Function in the
        //      background — it eventually backfills the server-side feed.
        //   2. In parallel, fetch this athlete's recent posts directly and
        //      splice them into the local feed in chronological order so the
        //      user sees content from them right away.
        //   3. As soon as the optimistic posts are visible, close the dialog
        //      so the action feels complete.
        //   4. When the backend follow + feed backfill finishes, refresh the
        //      feed to replace the optimistic posts with the canonical data.
        const capturedFoundUser = foundUser;
        setIsFoundUserFollowed(true);

        const followPromise = connectionState
          .followUser(capturedFoundUser.userId)
          .then(() => refreshFeed())
          .catch((err) => {
            console.error("Background follow failed:", err);
            // Drop the optimistic injection so we don't leave stale posts.
            setRecentlyFollowedId((curr) =>
              curr === capturedFoundUser.userId ? null : curr,
            );
            setPendingFollowedPosts((curr) =>
              curr.filter((item) => item.userId !== capturedFoundUser.userId),
            );
            setIsFoundUserFollowed(false);
            setError(err?.message || "Failed to follow user");
          });

        await injectFollowedUserPosts(capturedFoundUser.userId);

        // Close the Find Athlete dialog now that the feed has visible content
        // from this user — the real follow request continues in the background.
        setFoundUser(null);
        setSearchQuery("");
        setSuccessMessage(null);

        // Don't await `followPromise` — we want the dialog to close instantly.
        void followPromise;
        return;
      }

      // Refresh the feed now that the server-side work is done.
      await refreshFeed();

      setTimeout(() => {
        setFoundUser(null);
        setSearchQuery("");
        setSuccessMessage(null);
      }, 2000);
    } catch (err: any) {
      console.error("Error following/unfollowing user:", err);
      setError(err.message || "Failed to update follow status");
    } finally {
      setIsAdding(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSearchQuery("");
    setFoundUser(null);
    setError(null);
    setSuccessMessage(null);
    setIsFoundUserFollowed(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isSearching) {
      handleSearch();
    }
  };

  const handleViewFollowers = async () => {
    if (!currentUser) return;

    setIsLoadingFollowers(true);
    setIsFollowersDialogOpen(true);

    try {
      const followerIds = await getFollowers(currentUser.uid);

      const followersWithData = await Promise.all(
        followerIds.map(async (userId) => {
          const publicData = await getUserPublicData(userId);
          return { userId, publicData };
        }),
      );

      setFollowers(followersWithData);
    } catch (error) {
      console.error("Error loading followers:", error);
    } finally {
      setIsLoadingFollowers(false);
    }
  };

  const handleViewFollowing = async () => {
    if (!currentUser) return;

    setIsLoadingFollowing(true);
    setIsFollowingDialogOpen(true);

    try {
      const followingIds = await getFollowingList(currentUser.uid);

      const followingWithData = await Promise.all(
        followingIds.map(async (userId) => {
          const publicData = await getUserPublicData(userId);
          return { userId, publicData };
        }),
      );

      setFollowing(followingWithData);
    } catch (error) {
      console.error("Error loading following:", error);
    } finally {
      setIsLoadingFollowing(false);
    }
  };

  // Optimistically remove posts from an unfollowed user from the local list.
  // Also marks the user as `recentlyUnfollowedId` so the feed-sync effect does
  // not re-add their posts before the server-side cleanup propagates. The flag
  // is cleared automatically once they drop out of `feedEntries`.
  const removePostsFromUnfollowedUser = (userIdToUnfollow: string) => {
    setRecentlyUnfollowedId(userIdToUnfollow);
    setDisplayedPosts((prev) =>
      prev.filter((item) => item.userId !== userIdToUnfollow),
    );
  };

  // Optimistically fetch posts for a user we're about to follow and inject
  // them into the local feed list (in correct chronological order). The
  // injected posts persist until the real backend feed contains them, at
  // which point the sync effect drops the pending list.
  const injectFollowedUserPosts = async (
    userIdToFollow: string,
  ): Promise<void> => {
    try {
      const posts = await getUserPostsPaginated(userIdToFollow, 10);
      if (!posts || posts.length === 0) return;

      const mapped = posts.map((p) => ({
        id: `${userIdToFollow}-${p.id}`,
        post: { ...p, id: p.id },
        userId: userIdToFollow,
        timestamp: p.postTimestamp,
      }));

      setRecentlyFollowedId(userIdToFollow);
      setPendingFollowedPosts(mapped);
    } catch (err) {
      console.error("Error fetching posts for newly followed user:", err);
    }
  };

  const handleUnfollow = async (userIdToUnfollow: string) => {
    if (!currentUser) return;

    setUnfollowingUserId(userIdToUnfollow);

    // Optimistically remove the user's posts from the feed UI immediately
    // so the unfollow feels instant. The server work continues below.
    removePostsFromUnfollowedUser(userIdToUnfollow);
    setFollowing((prev) =>
      prev.filter((user) => user.userId !== userIdToUnfollow),
    );

    try {
      await connectionState.unfollowUser(userIdToUnfollow);
    } catch (error) {
      console.error("Error unfollowing user:", error);
      // Revert local hide on failure so the user can see / retry.
      setRecentlyUnfollowedId((curr) =>
        curr === userIdToUnfollow ? null : curr,
      );
    } finally {
      setUnfollowingUserId(null);
    }
  };

  // COMMENTED OUT: Suggested Athletes handlers
  // const handleFollowSuggestedAthlete = async (userId: string) => {
  //   if (!currentUser || followingSuggestedAthleteId === userId) return;

  //   console.log(`🔄 Starting follow process for athlete: ${userId}`);

  //   // Set loading state
  //   setFollowingSuggestedAthleteId(userId);

  //   try {
  //     // Use store method which handles optimistic updates
  //     await suggestedState.followAthlete(userId);

  //     console.log(`✅ Store followAthlete completed for: ${userId}`);

  //     // Check if the athlete is now marked as followed
  //     const athlete = suggestedAthletes.find((a) => a.userId === userId);
  //     console.log(
  //       `🔍 Athlete status after follow: ${
  //         athlete?.isFollowed ? "FOLLOWED" : "NOT FOLLOWED"
  //       }`
  //     );

  //     // Refresh feed after following
  //     setTimeout(async () => {
  //       console.log("Refreshing feed after following athlete...");
  //       await refreshFeed();
  //     }, 5000);
  //   } catch (error) {
  //     console.error("❌ Error following suggested athlete:", error);
  //   } finally {
  //     console.log(`🧹 Clearing loading state for: ${userId}`);
  //     // Clear loading state
  //     setFollowingSuggestedAthleteId(null);
  //   }
  // };

  // const handleDismissSuggestedAthlete = (userId: string) => {
  //   // Use store dismiss method
  //   suggestedState.dismissAthlete(userId);
  // };

  // Handle following Tori
  const handleFollowTori = async () => {
    if (!currentUser || isFollowingTori) return;

    setIsFollowingTori(true);
    setToriFollowError(null);

    try {
      // Lookup Tori's userId
      const toriData = await lookupUsername("toriwest");

      if (!toriData) {
        throw new Error(
          "Could not find Tori's account. Please try again later.",
        );
      }

      console.log(`🔄 Following Tori (userId: ${toriData.userId})`);

      // Follow Tori using connectionState hook (includes optimistic updates)
      await connectionState.followUser(toriData.userId);

      console.log("✅ Successfully followed Tori");

      // Show success state
      setToriFollowSuccess(true);

      // Refresh feed and WAIT for it to complete
      console.log("🔄 Refreshing feed to show Tori's posts...");
      await refreshFeed();
      console.log(
        "✅ Feed refresh complete - Tori's posts should now be loaded",
      );

      // Wait an additional 1.5 seconds to let user see the success message
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Dismiss card and mark as read
      setShowToriMessage(false);
      if (currentUser) {
        userService
          .updateUserProfile(currentUser.uid, {
            socialMessageRead: true,
          })
          .catch((err) => {
            console.error("Error marking message as read:", err);
          });
      }
    } catch (error) {
      console.error("❌ Error following Tori:", error);
      setToriFollowError(
        error instanceof Error
          ? error.message
          : "Failed to follow Tori. Please try again.",
      );
    } finally {
      setIsFollowingTori(false);
    }
  };

  // Handle dismissing the Tori message
  const handleDismissToriMessage = async () => {
    if (!currentUser || isDismissingMessage) return;

    setIsDismissingMessage(true);

    try {
      // Update user data to mark message as read
      await userService.updateUserProfile(currentUser.uid, {
        socialMessageRead: true,
      });

      // Hide the message
      setShowToriMessage(false);
    } catch (error) {
      console.error("Error dismissing Tori message:", error);
    } finally {
      setIsDismissingMessage(false);
    }
  };

  // Simple refresh - just refetch the user's feed.
  const refreshAllFeeds = useCallback(async () => {
    if (!effectiveUserId) return;
    try {
      await refreshFeed();
    } catch (error) {
      console.error("❌ Error refreshing feed:", error);
    }
  }, [effectiveUserId, refreshFeed]);

  // Pull to refresh handlers
  const handleTouchStart = useCallback((e: TouchEvent) => {
    // Check if BOTH window and container are at the top
    const windowScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;
    const containerScrollTop = containerRef.current?.scrollTop || 0;

    // Only allow pull-to-refresh when at the absolute top of the page
    if (windowScrollTop === 0 && containerScrollTop === 0) {
      setStartY(e.touches[0].clientY);
      setCanPull(true);
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      // Check both window and container scroll position
      const windowScrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const containerScrollTop = containerRef.current?.scrollTop || 0;

      if (
        !canPull ||
        startY === 0 ||
        windowScrollTop !== 0 ||
        containerScrollTop !== 0
      )
        return;

      const currentY = e.touches[0].clientY;
      const distance = currentY - startY;

      if (distance > 0 && distance <= 120) {
        e.preventDefault();
        setPullDistance(distance);
      }
    },
    [canPull, startY],
  );

  const handleTouchEnd = useCallback(async () => {
    if (
      pullDistance > REFRESH_THRESHOLD &&
      !isPullToRefresh &&
      !isGlobalLoading
    ) {
      // Trigger refresh
      setIsPullToRefresh(true);
      setPullDistance(0);

      try {
        // Refresh connections and both feed sources in parallel
        await Promise.all([
          connectionState.refresh(),
          refreshAllFeeds(), // This already uses Promise.all internally for feed+posts
        ]);

        // Success message removed - data fetches silently
      } catch (error) {
        console.error("Error refreshing feed:", error);
      } finally {
        setTimeout(() => {
          setIsPullToRefresh(false);
        }, 500);
      }
    }

    setPullDistance(0);
    setStartY(0);
    setCanPull(false);
  }, [
    pullDistance,
    isPullToRefresh,
    isGlobalLoading,
    refreshAllFeeds,
    REFRESH_THRESHOLD,
    connectionState,
  ]);

  // Add event listeners for pull to refresh
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Infinite scroll detection - using window scroll
  const handleScroll = useCallback(() => {
    // Get the distance from the bottom of the page
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    const scrollThreshold = 500; // Load more when 500px from bottom

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < scrollThreshold) {
      if (feedHasMore && !isGlobalLoading) {
        console.log("Loading more posts...", { feedHasMore });
        loadMoreFeed();
      }
    }
  }, [feedHasMore, isGlobalLoading, loadMoreFeed]);

  // Add scroll listener to window
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  // Local mirror of the feed used for optimistic UI updates (likes, comments,
  // delete). Source of truth is `feedEntries` from the useFeed hook.
  const [displayedPosts, setDisplayedPosts] = useState<any[]>([]);

  // Track the most recently unfollowed user so their posts stay hidden if the
  // feed re-syncs before the server-side cleanup propagates. Cleared as soon
  // as the user no longer appears in `feedEntries`.
  const [recentlyUnfollowedId, setRecentlyUnfollowedId] = useState<
    string | null
  >(null);

  // Optimistically injected posts for a user we just followed. Merged into
  // `displayedPosts` (in chronological order) until the real feed catches up
  // with them, at which point this is cleared.
  const [recentlyFollowedId, setRecentlyFollowedId] = useState<string | null>(
    null,
  );
  const [pendingFollowedPosts, setPendingFollowedPosts] = useState<any[]>([]);

  // Helper states for loading UI
  const isInitialLoad = isGlobalLoading && displayedPosts.length === 0;
  const isLoadingMore = isGlobalLoading && displayedPosts.length > 0;

  // Reset displayed posts when we switch which user's feed we're viewing.
  useEffect(() => {
    setDisplayedPosts([]);
  }, [effectiveUserId]);

  // Sync displayed posts from the latest feed entries.
  useEffect(() => {
    if (feedLoading) return;

    const mapped = feedEntries
      .filter((entry) => entry.authorId !== recentlyUnfollowedId)
      .map((entry) => ({
        id: `${entry.authorId}-${entry.postId}`,
        post: { ...entry.post, id: entry.postId },
        userId: entry.authorId,
        timestamp: entry.post.postTimestamp,
      }));

    // Merge in any optimistically-injected posts from a user we just followed
    // (deduped by composite id), then sort the whole list by timestamp desc so
    // the new posts land in their correct chronological slot.
    const existingIds = new Set(mapped.map((item) => item.id));
    const extras = pendingFollowedPosts.filter(
      (item) => !existingIds.has(item.id),
    );
    const merged =
      extras.length > 0
        ? [...mapped, ...extras].sort((a, b) => {
            const aMs = a.timestamp?.toMillis?.() ?? 0;
            const bMs = b.timestamp?.toMillis?.() ?? 0;
            return bMs - aMs;
          })
        : mapped;

    setDisplayedPosts(merged);

    // Once the server-side feed cleanup has propagated (the user is no longer
    // present in feedEntries), we can stop tracking them.
    if (
      recentlyUnfollowedId &&
      !feedEntries.some((entry) => entry.authorId === recentlyUnfollowedId)
    ) {
      setRecentlyUnfollowedId(null);
    }

    // Once every optimistically-injected followed-user post has appeared in
    // the real feed entries, drop the pending list so we stop merging it in.
    if (
      recentlyFollowedId &&
      pendingFollowedPosts.length > 0 &&
      pendingFollowedPosts.every((item) => existingIds.has(item.id))
    ) {
      setRecentlyFollowedId(null);
      setPendingFollowedPosts([]);
    }
  }, [
    feedEntries,
    feedLoading,
    recentlyUnfollowedId,
    recentlyFollowedId,
    pendingFollowedPosts,
  ]);
  const handleCommentChange = (
    postId: string,
    updatedComment: any,
    action: "create" | "edit" | "delete",
  ) => {
    setDisplayedPosts((prev) =>
      prev.map((item) => {
        if (item.post.id !== postId) return item;

        const existingComments = item.post.CommentsList || [];
        let newComments: any[] = existingComments;

        if (action === "edit") {
          newComments = existingComments.map((c: any) =>
            c.commentId === updatedComment.commentId ? updatedComment : c,
          );
        } else if (action === "create") {
          newComments = [...existingComments, updatedComment].sort(
            (a, b) => a.timestamp.toMillis() - b.timestamp.toMillis(),
          );
        } else if (action === "delete") {
          newComments = existingComments.filter(
            (c: any) => c.commentId !== updatedComment.commentId,
          );
        }

        return {
          ...item,
          post: { ...item.post, CommentsList: newComments },
        };
      }),
    );
  };

  const handleLikeChange = (
    postId: string,
    userId: string,
    action: "like" | "unlike",
  ) => {
    setDisplayedPosts((prev) =>
      prev.map((item) => {
        if (item.post.id !== postId) return item;

        const likesList = item.post.likesList || [];
        const updatedLikes =
          action === "like"
            ? [...likesList, userId]
            : likesList.filter((uid: any) => uid !== userId);

        return {
          ...item,
          post: { ...item.post, likesList: updatedLikes },
        };
      }),
    );
  };

  // Render Feed (simplified - only feed data)
  const renderMixedFeed = () => {
    return (
      <div className="space-y-4">
        {/* Removed unified loading indicator - data fetches silently */}

        {isInitialLoad ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {[0, 1, 2].map((index) => (
              <PostCardSkeleton key={`post-skeleton-${index}`} index={index} />
            ))}
          </motion.div>
        ) : feedError ? (
          <div className="flex flex-col items-center justify-center min-h-[80vh]">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-base-content font-semibold text-lg font-grotesk mb-2">
              Something went wrong
            </p>
            <p className="text-base-content/60 text-center mb-6 max-w-md">
              {feedError}
            </p>
            <button
              onClick={refreshAllFeeds}
              className="px-6 py-3 bg-neutral text-white rounded-full hover:bg-neutral/90 transition-colors font-grotesk font-medium"
            >
              Try Again
            </button>
          </div>
        ) : displayedPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-8">
            {/* Main Message */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-base-content mb-3 font-grotesk">
                Welcome to your feed!
              </h2>
              <p className="text-base-content/60 text-lg mb-4 max-w-md leading-relaxed">
                Connect with athletes & training partners to see their training
                updates.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile / single column layout */}
            <div className="lg:hidden space-y-4">
              {displayedPosts.map((item) => (
                <PostCard
                  key={item.id}
                  post={item.post}
                  userId={item.userId}
                  currentUserId={currentUser?.uid}
                  showOwnerActions={item.userId === currentUser?.uid}
                  onPostUpdate={async () => {
                    setPostLoadingId(item.id);
                    await refreshAllFeeds();
                    setPostLoadingId(null);
                  }}
                  onPostDelete={() => {
                    setDisplayedPosts((prev) =>
                      prev.filter((p) => p.id !== item.id),
                    );
                    refreshAllFeeds();
                  }}
                  onFeedRefresh={async () => {
                    await connectionState.refresh();
                    await refreshFeed();
                  }}
                  onUnfollowOptimistic={removePostsFromUnfollowedUser}
                  pageLoading={postLoadingId === item.id}
                  onCommentChange={handleCommentChange}
                  onLikeChange={handleLikeChange}
                />
              ))}
            </div>

            {/* Desktop 3-column layout: distribute round-robin so newest posts
                fill rows left-to-right (left top, middle top, right top, then
                left next row, etc.) rather than top-to-bottom per column. */}
            <div className="hidden lg:grid lg:grid-cols-3 lg:gap-4">
              {[0, 1, 2].map((colIndex) => (
                <div key={`col-${colIndex}`} className="space-y-4">
                  {displayedPosts
                    .filter((_, i) => i % 3 === colIndex)
                    .map((item) => (
                      <PostCard
                        key={item.id}
                        post={item.post}
                        userId={item.userId}
                        currentUserId={currentUser?.uid}
                        showOwnerActions={item.userId === currentUser?.uid}
                        onPostUpdate={async () => {
                          setPostLoadingId(item.id);
                          await refreshAllFeeds();
                          setPostLoadingId(null);
                        }}
                        onPostDelete={() => {
                          setDisplayedPosts((prev) =>
                            prev.filter((p) => p.id !== item.id),
                          );
                          refreshAllFeeds();
                        }}
                        onFeedRefresh={async () => {
                          await connectionState.refresh();
                          await refreshFeed();
                        }}
                        onUnfollowOptimistic={removePostsFromUnfollowedUser}
                        pageLoading={postLoadingId === item.id}
                        onCommentChange={handleCommentChange}
                        onLikeChange={handleLikeChange}
                      />
                    ))}
                </div>
              ))}
            </div>

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="p-8 text-center my-4 animate-pulse">
                <div className="flex items-center justify-center gap-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent justify-f"></div>
                  <div>
                    <p className="text-base-content font-semibold font-grotesk">
                      Loading more posts...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* End of feed message */}
            {!feedHasMore && !isLoadingMore && displayedPosts.length > 0 && (
              <div className="p-6 text-center my-4">
                <div className="flex items-center justify-center gap-2 text-base-content/60">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <p className="text-sm font-grotesk">
                    You've reached the end of your feed
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (!isRestrictedByAge && suspensionCheckLoading) {
    return (
      <div className="pt-10 w-full min-h-screen relative">
        <Header />
        <div className="flex items-center justify-center py-32">
          <span className="loading loading-spinner loading-lg text-base-content/40"></span>
        </div>
        <BottomNavBar />
      </div>
    );
  }

  if (isRestrictedByAge) {
    return (
      <div className="pt-10 w-full min-h-screen relative">
        <Header />
        <div className="mx-auto max-w-lg px-6 pt-16 pb-24">
          <div className="bg-base-100 border border-base-300 rounded-2xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-base-content/60"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 11c0-1.657 1.343-3 3-3s3 1.343 3 3-1.343 3-3 3m-6 4v1a2 2 0 002 2h8a2 2 0 002-2v-7a2 2 0 00-2-2h-1M9 11V7a3 3 0 016 0v4"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-base-content font-grotesk mb-2">
              Social is unavailable
            </h1>
            <p className="text-base-content/70 text-sm leading-relaxed mb-6">
              You must be at least 16 years old to access the social feature.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-5 py-2.5 bg-neutral text-neutral-content rounded-full font-grotesk text-sm font-medium hover:bg-neutral/90 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
        <BottomNavBar />
      </div>
    );
  }
  if (isSuspended && socialBlock) {
    const categoryLabel =
      socialBlock.blockedReasonCategory === "legal"
        ? "Legal basis"
        : "Community Guidelines";
    const reasonText = socialBlock.blockedReason
      ? suspensionReasonLabels[socialBlock.blockedReason] ??
        socialBlock.blockedReason
      : "—";
    const effectiveText = socialBlock.blockedAt?.toDate
      ? socialBlock.blockedAt.toDate().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "—";
    const durationText =
      socialBlock.blockedDuration == null
        ? "Permanent"
        : `${socialBlock.blockedDuration} days`;

    return (
      <div className="pt-10 w-full min-h-screen relative">
        <Header />
        <div className="mx-auto max-w-lg px-6 pt-16 pb-24">
          <div className="bg-base-100 border border-base-300 rounded-2xl shadow-sm p-8">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-base-content font-grotesk mb-3 text-center">
              Social features suspended
            </h1>
            <p className="text-base-content/70 text-sm leading-relaxed mb-5 text-center">
              Your access to Peak's social features has been suspended.
            </p>
            <div className="bg-base-200 rounded-lg p-4 text-sm text-base-content/80 space-y-2 mb-5">
              <p>
                <span className="font-semibold">Reason:</span> {reasonText} (
                {categoryLabel})
              </p>
              <p>
                <span className="font-semibold">Effective:</span>{" "}
                {effectiveText}
              </p>
              <p>
                <span className="font-semibold">Duration:</span> {durationText}
              </p>
            </div>
            <p className="text-base-content/70 text-sm leading-relaxed mb-5">
              During this period you can't create posts, comment or interact
              with other users. Your training logs remain available.
            </p>
            <div className="pt-4 border-t border-base-300">
              <h2 className="text-base font-semibold text-base-content font-grotesk mb-1">
                Appeal
              </h2>
              <p className="text-base-content/70 text-sm leading-relaxed">
                If you believe this decision is incorrect, you may submit an
                appeal.
              </p>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full px-5 py-2.5 bg-neutral text-neutral-content rounded-full font-grotesk text-sm font-medium hover:bg-neutral/90 transition-colors"
              >
                Back to Dashboard
              </button>
              <button
                onClick={() => navigate("/support")}
                className="w-full px-5 py-2.5 bg-base-200 text-base-content rounded-full font-grotesk text-sm font-medium hover:bg-base-300 transition-colors"
              >
                Help & Support
              </button>
            </div>
          </div>
        </div>
        <BottomNavBar />
      </div>
    );
  }

  return (
    <div className="pt-10 w-full min-h-screen relative">
      <Header />

      <div
        ref={containerRef}
        className="mx-auto max-w-lg lg:max-w-5xl py-6 sm:px-0 overflow-y-auto pb-24"
        style={{
          transform: `translateY(${Math.min(pullDistance * 0.5, 40)}px)`,
          transition: pullDistance === 0 ? "transform 0.3s ease-out" : "none",
        }}
      >
        {/* Removed Pull to Refresh Indicator - data fetches silently */}

        {/* Success message removed - data fetches silently */}

        {/* Admin viewing indicator */}
        {isViewingAsAdmin && (
          <div className="bg-blue-500/10 border-blue-200 border p-3 mb-4 mx-4">
            <div className="flex items-center space-x-2 text-blue-700">
              <EyeIcon className="h-5 w-5" />
              <span className="text-sm font-medium">
                Viewing{" "}
                {viewedUser?.username
                  ? `${viewedUser.username}'s`
                  : "another user's"}{" "}
                feed
              </span>
            </div>
          </div>
        )}

        <div className="mb-3 m-4 px-8 sm:px-0 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center mb-2">
              <h3 className="text-3xl">Social</h3>
              <div className="text-xs bg-lime/40 font-grotesk text-black font-bold px-2 py-1 rounded-full ml-2">
                beta
              </div>
            </div>
            {/* TEMPORARY: Refresh button for testing */}
            <button
              onClick={async () => {
                console.log("🔄 Manual refresh triggered");
                await Promise.all([
                  connectionState.refresh(),
                  refreshAllFeeds(),
                ]);
                console.log("✅ Manual refresh complete");
              }}
              disabled={isGlobalLoading}
              className="px-3 py-1.5 text-xs  text-base-content/80 rounded-lg hover:bg-base-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk font-medium flex items-center gap-1.5"
            >
              {isGlobalLoading ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-base-content/80"></div>
                  Loading...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>
        <div></div>

        {/* Search Field Above Suggested Athletes */}
        {!isViewingAsAdmin && (
          <div className="px-8 sm:px-0 mb-3">
            <motion.button
              onClick={() => setIsDialogOpen(true)}
              className="w-full bg-base-100 border rounded-lg p-3 text-left flex items-center gap-2.5 transition-colors shadow-sm"
              variants={searchFieldVariants}
              initial="idle"
              whileHover={prefersReducedMotion ? "idle" : "hover"}
              whileTap={prefersReducedMotion ? "idle" : "tap"}
              style={{
                borderColor: "oklch(var(--b3))", // This will be animated by variants
              }}
            >
              <motion.div variants={searchIconVariants}>
                <MagnifyingGlassIcon className="h-4 w-4 text-base-content/50" />
              </motion.div>
              <span className="text-base-content/60 font-grotesk text-sm">
                Search for athletes...
              </span>
            </motion.button>
          </div>
        )}

        {/* COMMENTED OUT: Tori Welcome Message - only show if not read */}
        {/* {!checkingToriMessage && showToriMessage && (
          <motion.div
            className={`shadow-sm border text-xs p-4 mb-4 mx-8 rounded-lg transition-colors ${
              toriFollowSuccess
                ? "bg-green-500/10 border-green-200"
                : toriFollowError
                ? "bg-red-500/10 border-red-200"
                : "bg-base-100 border-base-300"
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex justify-between items-center">
              <p>Hey @{currentUserPublicData?.username || "there"},</p>
              <button
                onClick={handleDismissToriMessage}
                disabled={isDismissingMessage}
                className="p-1 rounded-full hover:bg-base-200 bg-base-300  transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                aria-label="Dismiss message"
              >
                {isDismissingMessage ? (
                  <div className="w-4 h-4 animate-spin rounded-full border-2 border-base-content/50 border-t-transparent"></div>
                ) : (
                  <XMarkIcon className="h-4 w-4 text-base-content" />
                )}
              </button>
            </div>
            <p>
              <br></br> My name is Tori. I built PEAK^ because I needed a way to
              track my training data and realised many other athletes did too.{" "}
              <br></br>
              <br></br> My goal is to make this the most practical,
              research-backed and accessible app for athletes no matter your
              sport, country or level. <br></br>
              <br></br>In 2024, I had the privilege of going to the Olympics and
              worked my way there from humble beginnings. I understand the
              hardship and beauty of sport. <br></br>
              <br></br>Your training and performance data tells a story. It's
              powerful. I am on a mission to build the best app for athletes and
              help you unlock the value of your data.
              <br></br>
              <br></br>
              Tori
            </p>

            Action buttons:
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleFollowTori}
                disabled={isFollowingTori}
                className=" bg-neutral text-neutral-content px-4 py-2 font-grotesk rounded-full text-xs font-medium hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 10 }}
                  className="mx-auto w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-3"
                >
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </motion.div>
                <p className="text-green-700 dark:text-green-400 font-semibold mb-1 text-sm">
                  You're now following Tori!
                </p>
                <p className="text-green-700 text-xs mb-2">
                  {isGlobalLoading ? "Loading Tori's posts..." : "Posts loaded successfully!"}
                </p>
                {isGlobalLoading && (
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                  </div>
                )}
              </motion.div>
            ) : toriFollowError ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-2"
              >
                <div className="flex items-start gap-2 mb-3">
                  <svg
                    className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="text-red-700 dark:text-red-400 font-semibold text-sm mb-1">
                      Could not follow Tori
                    </p>
                    <p className="text-red-700">{toriFollowError}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setToriFollowError(null);
                      handleFollowTori();
                    }}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md text-xs font-medium hover:bg-red-700 transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => setToriFollowError(null)}
                    className="flex-1 bg-base-100 text-red-700 px-4 py-2 rounded-md text-xs font-medium hover:bg-red-500/10 transition-colors border border-red-200"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            ) : (
              <>
                <p>
                  Hey @{currentUserPublicData?.username || "there"},<br></br>
                  <br></br> My name is Tori. I built PEAK^ because I needed a way to
                  track my training data and realised many other athletes did too.{" "}
                  <br></br>
                  <br></br> My goal is to make this the most practical,
                  research-backed and accessible app for athletes no matter your
                  sport, country or level. <br></br>
                  <br></br>In 2024, I had the privilege of going to the Olympics and
                  worked my way there from humble beginnings. I understand the
                  hardship and beauty of sport. <br></br>
                  <br></br>
                  Before I was an Olympian, I was more successful as a software
                  developer than athlete. Sport was a passion not a job. Now with
                  PEAK^ and you guys using the app, I get to merge code and sport
                  together.
                  <br></br>
                  <br></br>In my journey I have realised our data tells a story.
                  It's powerful. I am on a mission to build the best app for
                  athletes and help you unlock the value of your data.
                  <br></br>
                  <br></br>
                  You can email me at <strong>tori@peak.app</strong> with feedback
                  or feel free to follow my public training journal here in the
                  social section.
                  <br></br>
                  <br></br>
                  Tori
                </p>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleFollowTori}
                    disabled={isFollowingTori}
                    className="flex-1 bg-neutral text-neutral-content px-4 py-2 rounded-md text-xs font-medium hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isFollowingTori ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-base-100"></div>
                        Following...
                      </>
                    ) : (
                      "Follow Tori"
                    )}
                  </button>
                  <button
                    onClick={handleDismissToriMessage}
                    disabled={isDismissingMessage}
                    className="flex-1 bg-base-200 text-base-content/80 px-4 py-2 rounded-md text-xs font-medium hover:bg-base-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDismissingMessage ? "..." : "Read"}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )} */}

        {/* Suggested Athletes Section */}
        {/* TEMPORARILY COMMENTED OUT
        {(isGlobalLoading ||
          suggestedAthletes.filter((athlete) => !athlete.isDismissed).length >
            0) && (
          <div className="mb-5 px-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-md font-bold text-base-content font-grotesk">
                Suggested Athletes
              </h2>
            </div>

            <div className="relative">
              <div className="athletes-scroll-container flex gap-4 overflow-x-auto pb-2">
                <AnimatePresence mode="wait">
                  {isGlobalLoading && suggestedAthletes.length === 0 ? (
                    <motion.div key="skeleton-loading" className="flex gap-4">
                      {[0, 1, 2].map((index) => (
                        <AthleteCardSkeleton
                          key={`skeleton-${index}`}
                          index={index}
                        />
                      ))}
                    </motion.div>
                  ) : suggestedAthletes.filter(
                      (athlete) => !athlete.isDismissed
                    ).length === 0 ? (
                    <motion.div
                      key="no-athletes"
                      className="text-center w-full py-4 text-base-content/60 text-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      No suggested athletes available at the moment
                    </motion.div>
                  ) : (
                    <motion.div key="athletes-list" className="flex gap-4">
                      {suggestedAthletes
                        .filter((athlete) => !athlete.isDismissed)
                        .map((athlete, index) => (
                          <motion.div
                            key={athlete.userId}
                            className="flex-shrink-0 bg-base-100 rounded-lg shadow-sm border border-base-300 p-5 w-44 relative"
                            variants={animationVariants.athleteCard}
                            initial="hidden"
                            animate="visible"
                            exit={{
                              opacity: 0,
                              scale: 0.8,
                              transition: {
                                duration: prefersReducedMotion ? 0 : 0.2,
                              },
                            }}
                            whileHover="hover"
                            custom={index}
                            layout={!prefersReducedMotion}
                          >
                            <motion.button
                              onClick={() =>
                                handleDismissSuggestedAthlete(athlete.userId)
                              }
                              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-base-200 hover:bg-base-300 flex items-center justify-center transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <svg
                                className="w-3 h-3 text-base-content/50"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </motion.button>

                            <div className="flex justify-center mb-3">
                              {athlete.publicData?.imageUrlNormal || athlete.publicData?.imageUrl ? (
                                <img
                                  src={athlete.publicData.imageUrlNormal || athlete.publicData.imageUrl}
                                  alt={athlete.publicData.username || "Athlete"}
                                  className="w-16 h-16 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-full bg-neutral flex items-center justify-center">
                                  <span className="text-white text-lg font-bold font-grotesk">
                                    {athlete.publicData?.username
                                      ?.charAt(0)
                                      .toUpperCase() || "?"}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="text-center mb-4">
                              <h3 className="font-semibold text-base-content font-grotesk text-base mb-1">
                                {athlete.publicData?.username || "Unknown"}
                              </h3>
                              <p className="text-sm text-base-content/60 font-grotesk">
                                {getSportNames(athlete.publicData) ||
                                  "Featured"}
                              </p>
                            </div>

                            <motion.button
                              onClick={() =>
                                handleFollowSuggestedAthlete(athlete.userId)
                              }
                              disabled={
                                athlete.isFollowed ||
                                followingSuggestedAthleteId === athlete.userId
                              }
                              className={`w-full py-3 rounded-md font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                                athlete.isFollowed
                                  ? "bg-blue-500/15 text-blue-700 cursor-not-allowed"
                                  : followingSuggestedAthleteId ===
                                    athlete.userId
                                  ? "bg-blue-500/15 text-blue-700 cursor-not-allowed"
                                  : "bg-base-300 text-base-content/80 hover:bg-base-300"
                              }`}
                              variants={animationVariants.followButton}
                              whileHover={
                                !athlete.isFollowed &&
                                followingSuggestedAthleteId !== athlete.userId
                                  ? "hover"
                                  : "idle"
                              }
                              whileTap={
                                !athlete.isFollowed &&
                                followingSuggestedAthleteId !== athlete.userId
                                  ? "tap"
                                  : "idle"
                              }
                            >
                              {followingSuggestedAthleteId ===
                              athlete.userId ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                                  Following...
                                </>
                              ) : athlete.isFollowed ? (
                                "Followed"
                              ) : (
                                "Follow"
                              )}
                            </motion.button>
                          </motion.div>
                        ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-base-200 to-transparent pointer-events-none"></div>
            </div>
          </div>
        )}
        */}

        {/* Section Divider */}
        <div className="px-4 py-2">
          <div className="h-px bg-gradient-to-r from-transparent via-base-300 to-transparent"></div>
        </div>

        {/* Connection Stats Section */}

        {/* Mixed Feed */}
        <div className="pt-3">{renderMixedFeed()}</div>
      </div>

      {/* Followers Dialog */}
      {isFollowersDialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50">
          {/* Desktop Version */}
          <div className="hidden md:flex items-center justify-center min-h-full p-4">
            <div className="bg-base-100 rounded-2xl shadow-2xl max-w-lg w-full p-8 max-h-[85vh] flex flex-col">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-base-content font-grotesk">
                    Followers
                  </h3>
                  <p className="text-base-content/60 text-sm mt-1">
                    {followerCount} people following you
                  </p>
                </div>
                <button
                  onClick={() => setIsFollowersDialogOpen(false)}
                  className="p-2 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="overflow-y-auto flex-1 -mx-2 px-2">
                {isLoadingFollowers ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-content mx-auto mb-4"></div>
                      <p className="text-base-content/70 font-grotesk">
                        Loading followers...
                      </p>
                    </div>
                  </div>
                ) : followers.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8 text-base-content/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-base-content/70 font-grotesk font-medium">
                      No followers yet
                    </p>
                    <p className="text-sm text-base-content/50 mt-1">
                      Start sharing your workouts to attract followers
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {followers.map((user) => (
                      <div
                        key={user.userId}
                        className="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group"
                      >
                        {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                          <img
                            src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                            alt={user.publicData.username}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                            <span className="text-white text-sm font-semibold font-grotesk">
                              {user.publicData?.username
                                ?.charAt(0)
                                .toUpperCase() ||
                                user.userId.slice(-1).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-base-content font-grotesk truncate">
                            {user.publicData?.username ||
                              `User ${user.userId.slice(-4)}`}
                          </p>
                          {getSportNames(user.publicData) && (
                            <p className="text-sm text-base-content/60 truncate">
                              {getSportNames(user.publicData)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Version */}
          <div className="md:hidden fixed inset-0 bg-base-100 transform transition-all duration-300 ease-out flex flex-col">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-8 h-0.5 bg-base-300 rounded-full"></div>
            </div>

            <div className="flex justify-between items-center px-4 pb-3">
              <div>
                <h3 className="text-lg font-bold text-base-content font-grotesk">
                  Followers
                </h3>
                <p className="text-base-content/60 text-xs mt-0.5">
                  {followerCount} people following you
                </p>
              </div>
              <button
                onClick={() => setIsFollowersDialogOpen(false)}
                className="p-1.5 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 px-4 pb-6 overflow-y-auto">
              {isLoadingFollowers ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-base-content mx-auto mb-3"></div>
                    <p className="text-base-content/70 font-grotesk text-sm">
                      Loading followers...
                    </p>
                  </div>
                </div>
              ) : followers.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-base-content/50"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-base-content/70 font-grotesk font-medium text-sm">
                    No followers yet
                  </p>
                  <p className="text-xs text-base-content/50 mt-1">
                    Start sharing your workouts to attract followers
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {followers.map((user) => (
                    <div
                      key={user.userId}
                      className="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group"
                    >
                      {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                        <img
                          src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                          alt={user.publicData.username}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                          <span className="text-white text-sm font-semibold font-grotesk">
                            {user.publicData?.username
                              ?.charAt(0)
                              .toUpperCase() ||
                              user.userId.slice(-1).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-base-content font-grotesk text-sm truncate">
                          {user.publicData?.username ||
                            `User ${user.userId.slice(-4)}`}
                        </p>
                        {getSportNames(user.publicData) && (
                          <p className="text-xs text-base-content/60 truncate">
                            {getSportNames(user.publicData)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Following Dialog */}
      {isFollowingDialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50">
          {/* Desktop Version */}
          <div className="hidden md:flex items-center justify-center min-h-full p-4">
            <div className="bg-base-100 rounded-2xl shadow-2xl max-w-lg w-full p-8 max-h-[85vh] flex flex-col">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-base-content font-grotesk">
                    Following
                  </h3>
                  <p className="text-base-content/60 text-sm mt-1">
                    You're following {followingCount} people
                  </p>
                </div>
                <button
                  onClick={() => setIsFollowingDialogOpen(false)}
                  className="p-2 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="overflow-y-auto flex-1 -mx-2 px-2">
                {isLoadingFollowing ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-content mx-auto mb-4"></div>
                      <p className="text-base-content/70 font-grotesk">
                        Loading following...
                      </p>
                    </div>
                  </div>
                ) : following.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8 text-base-content/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-base-content/70 font-grotesk font-medium">
                      Not following anyone yet
                    </p>
                    <p className="text-sm text-base-content/50 mt-1">
                      Discover and follow athletes to build your network
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {following.map((user) => (
                      <div
                        key={user.userId}
                        className="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group"
                      >
                        {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                          <img
                            src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                            alt={user.publicData.username}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                            <span className="text-white text-sm font-semibold font-grotesk">
                              {user.publicData?.username
                                ?.charAt(0)
                                .toUpperCase() ||
                                user.userId.slice(-1).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-base-content font-grotesk truncate">
                            {user.publicData?.username ||
                              `User ${user.userId.slice(-4)}`}
                          </p>
                          {getSportNames(user.publicData) && (
                            <p className="text-sm text-base-content/60 truncate">
                              {getSportNames(user.publicData)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleUnfollow(user.userId)}
                          disabled={unfollowingUserId === user.userId}
                          className="px-3 py-1.5 text-xs bg-base-200 text-base-content/80 rounded-lg hover:bg-red-500/10 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk font-medium"
                        >
                          {unfollowingUserId === user.userId
                            ? "..."
                            : "Unfollow"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Version */}
          <div className="md:hidden fixed inset-0 bg-base-100 transform transition-all duration-300 ease-out flex flex-col">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-8 h-0.5 bg-base-300 rounded-full"></div>
            </div>

            <div className="flex justify-between items-center px-4 pb-3">
              <div>
                <h3 className="text-lg font-bold text-base-content font-grotesk">
                  Following
                </h3>
                <p className="text-base-content/60 text-xs mt-0.5">
                  You're following {followingCount} people
                </p>
              </div>
              <button
                onClick={() => setIsFollowingDialogOpen(false)}
                className="p-1.5 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 px-4 pb-6 overflow-y-auto">
              {isLoadingFollowing ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-base-content mx-auto mb-3"></div>
                    <p className="text-base-content/70 font-grotesk text-sm">
                      Loading following...
                    </p>
                  </div>
                </div>
              ) : following.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-base-content/50"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-base-content/70 font-grotesk font-medium text-sm">
                    Not following anyone yet
                  </p>
                  <p className="text-xs text-base-content/50 mt-1">
                    Discover and follow athletes to build your network
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {following.map((user) => (
                    <div
                      key={user.userId}
                      className="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group"
                    >
                      {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                        <img
                          src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                          alt={user.publicData.username}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                          <span className="text-white text-sm font-semibold font-grotesk">
                            {user.publicData?.username
                              ?.charAt(0)
                              .toUpperCase() ||
                              user.userId.slice(-1).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-base-content font-grotesk text-sm truncate">
                          {user.publicData?.username ||
                            `User ${user.userId.slice(-4)}`}
                        </p>
                        {getSportNames(user.publicData) && (
                          <p className="text-xs text-base-content/60 truncate">
                            {getSportNames(user.publicData)}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnfollow(user.userId)}
                        disabled={unfollowingUserId === user.userId}
                        className="px-2 py-1 text-xs bg-base-200 text-base-content/80 rounded-md hover:bg-red-500/10 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk font-medium"
                      >
                        {unfollowingUserId === user.userId ? "..." : "Unfollow"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search User Dialog */}
      {isDialogOpen && (
        <div className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-md border border-base-200 max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-semibold text-base-content">
                  Find Athlete
                </h3>
                <p className="text-sm text-base-content/60">
                  Search athletes by username
                </p>
              </div>
              <button
                onClick={handleCloseDialog}
                className="text-base-content/50 hover:text-base-content/70 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <input
                  id="username-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="search by username"
                  className="w-full px-4 py-3 pr-12 placeholder:text-base-content/70 bg-base-300 border border-base-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-base-content focus:border-base-content transition-all"
                  disabled={isSearching}
                />
                <button
                  onClick={handleSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-base-content/70 hover:text-base-content/70 disabled:text-base-content/40 disabled:cursor-not-allowed transition-colors"
                >
                  {isSearching ? (
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                {successMessage}
              </div>
            )}

            {/* Found User Display */}
            {foundUser && !successMessage && (
              <div className="mb-4 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group">
                <div className="flex items-center gap-3">
                  {/* Clickable Avatar + User Info */}
                  <button
                    onClick={() => {
                      handleCloseDialog();
                      navigate(`/profile/${foundUser.userId}`);
                    }}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    {/* User Avatar */}
                    {foundUser.publicData?.imageUrlTiny || foundUser.publicData?.imageUrl ? (
                      <img
                        src={foundUser.publicData.imageUrlTiny || foundUser.publicData.imageUrl}
                        alt={foundUser.username}
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                        <span className="text-white text-sm font-semibold font-grotesk">
                          {foundUser.username?.charAt(0).toUpperCase() || "?"}
                        </span>
                      </div>
                    )}

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-base-content font-grotesk truncate hover:underline">
                        {foundUser.username}
                      </p>
                      {getSportNames(foundUser.publicData) && (
                        <p className="text-sm text-base-content/60 truncate">
                          {getSportNames(foundUser.publicData)}
                        </p>
                      )}
                    </div>
                  </button>

                  {/* Follow/Unfollow Button */}
                  <button
                    onClick={handleAddUser}
                    disabled={isAdding}
                    className="px-3 py-1.5 text-xs bg-base-200 text-base-content/80 rounded-lg hover:bg-blue-500/10 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk font-medium"
                  >
                    {isAdding
                      ? "..."
                      : isFoundUserFollowed
                        ? "Unfollow"
                        : "Follow"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <BottomNavBar />
    </div>
  );
};

export default Connections;
